"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import ProcessingStepsButton from "@/components/ui/ProcessingStepsButton";
import {
  isSimulationHref,
  SIMULATION_PROCESSING_EVENT,
  SIMULATION_PROCESSING_STEPS,
  simulationProcessingEventDetail,
  startSimulationProcessing,
} from "@/lib/simulation-processing";

type ProcessingState = { step: number; startedAt: number } | null;

export default function SimulationProcessingHost() {
  const pathname = usePathname();
  const [processing, setProcessing] = useState<ProcessingState>(null);
  const startedAt = processing?.startedAt ?? null;

  useEffect(() => {
    const onProcessing = (event: Event) => {
      const detail = simulationProcessingEventDetail(event);
      if (!detail) return;
      if (detail.action === "start") {
        setProcessing({ step: 0, startedAt: Date.now() });
        return;
      }
      if (detail.action === "finish") {
        setProcessing(null);
        return;
      }
      setProcessing((current) => (
        current
          ? { ...current, step: Math.max(current.step, detail.step) }
          : current
      ));
    };
    window.addEventListener(SIMULATION_PROCESSING_EVENT, onProcessing);
    return () => window.removeEventListener(SIMULATION_PROCESSING_EVENT, onProcessing);
  }, []);

  useEffect(() => {
    const onDocumentClick = (event: MouseEvent) => {
      if (pathname.startsWith("/simulation")) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest("a[href]");
      if (!(anchor instanceof HTMLAnchorElement)) return;
      if (!isSimulationHref(anchor.href)) return;
      startSimulationProcessing();
    };
    document.addEventListener("click", onDocumentClick, true);
    return () => document.removeEventListener("click", onDocumentClick, true);
  }, [pathname]);

  useEffect(() => {
    if (startedAt == null) return;
    const timeout = window.setTimeout(() => setProcessing(null), 30_000);
    return () => window.clearTimeout(timeout);
  }, [startedAt]);

  if (!processing) return null;
  return (
    <div className="fixed inset-0 z-[1000] flex items-start justify-center p-6 pt-12">
      <ProcessingStepsButton
        functionName="Simulations"
        steps={SIMULATION_PROCESSING_STEPS}
        currentStep={processing.step}
      />
    </div>
  );
}
